/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListExternalCredentialsRequest,
  ListExternalCredentialsSecurity,
  QueryParamProvider,
} from "../models/operations/listexternalcredentials.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListExternalCredentialsQuery,
  ListExternalCredentialsQueryData,
  prefetchListExternalCredentials,
  queryKeyListExternalCredentials,
} from "./listExternalCredentials.core.js";
export {
  buildListExternalCredentialsQuery,
  type ListExternalCredentialsQueryData,
  prefetchListExternalCredentials,
  queryKeyListExternalCredentials,
};

export type ListExternalCredentialsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listExternalCredentials externalCredentials
 *
 * @remarks
 * List the organization's external credentials (provider-independent summary). Optionally filter by provider. Requires org:read.
 */
export function useListExternalCredentials(
  request?: ListExternalCredentialsRequest | undefined,
  security?: ListExternalCredentialsSecurity | undefined,
  options?: QueryHookOptions<
    ListExternalCredentialsQueryData,
    ListExternalCredentialsQueryError
  >,
): UseQueryResult<
  ListExternalCredentialsQueryData,
  ListExternalCredentialsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListExternalCredentialsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listExternalCredentials externalCredentials
 *
 * @remarks
 * List the organization's external credentials (provider-independent summary). Optionally filter by provider. Requires org:read.
 */
export function useListExternalCredentialsSuspense(
  request?: ListExternalCredentialsRequest | undefined,
  security?: ListExternalCredentialsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListExternalCredentialsQueryData,
    ListExternalCredentialsQueryError
  >,
): UseSuspenseQueryResult<
  ListExternalCredentialsQueryData,
  ListExternalCredentialsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListExternalCredentialsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListExternalCredentialsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider?: QueryParamProvider | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ListExternalCredentialsQueryData,
): ListExternalCredentialsQueryData | undefined {
  const key = queryKeyListExternalCredentials(...queryKeyBase);

  return client.setQueryData<ListExternalCredentialsQueryData>(key, data);
}

export function invalidateListExternalCredentials(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider?: QueryParamProvider | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalCredentials", "list", ...queryKeyBase],
  });
}

export function invalidateAllListExternalCredentials(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalCredentials", "list"],
  });
}
